import { useCallback, useMemo, useState } from 'react';

import { useMultipleFormatInsights } from 'src/services/useMultipleFormatInsights';
import { useCurrentCustomerId } from 'src/store/customer';

export const useCustomFormatProps = ({
  localEditorRef,
  setSuccessMessage,
  setEditMode,
  readOnly,
  customFormatIds,
}) => {
  const currentCustomerId = useCurrentCustomerId();

  const [refreshKey, setRefreshKey] = useState(0);
  const increaseRefreshKey = useCallback(() => {
    setRefreshKey((prev) => prev + 1);
  }, []);

  // Use the provided custom format IDs
  const formatIds = useMemo(() => customFormatIds || [], [customFormatIds]);

  // Get insights for all format IDs using the custom hook
  const { formatResults, loading: isLoading } = useMultipleFormatInsights(
    {
      customerId: currentCustomerId,
      customFormatIds: formatIds,
      html: localEditorRef?.current?.getHtml(),
    },
    {
      disabled: formatIds.length === 0,
      refreshKey,
    }
  );

  // Create individual fix handlers for each format
  const onFormatFixClick = useCallback(
    (formatId) => {
      return (event) => {
        event.stopPropagation();
        const formatResult = formatResults.find(({ formatId: id }) => id === formatId);
        if (formatResult?.fixedHtml) {
          localEditorRef.current.replaceHtml(formatResult.fixedHtml);
          setEditMode(true);
          setSuccessMessage(`Updated to comply with format guidelines`);
        }
      };
    },
    [formatResults, localEditorRef, setEditMode, setSuccessMessage]
  );

  return useMemo(
    () => ({
      loading: isLoading,
      formatResults,
      customFormatIds: formatIds, // Use actual format IDs
      readOnly,
      onMounted: increaseRefreshKey,
      onFormatFixClick,
    }),
    [isLoading, formatResults, formatIds, readOnly, increaseRefreshKey, onFormatFixClick]
  );
};
